import React, { useMemo } from 'react';
import { Question } from '../../../types';
import { generateSupportCoverageReport, FamilyCoverageStatus } from '../../../support/supportCoverageReport';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { CheckCircle2, AlertTriangle, AlertOctagon, PieChart, HelpCircle, Eye, FileQuestion, LayoutList, XCircle } from 'lucide-react';
interface TaxonomyCoverageProps {
  questions: Question[];
}
export function TaxonomyCoverage({
  questions
}: TaxonomyCoverageProps) {
  const report = useMemo(() => generateSupportCoverageReport(questions), [questions]);
  const {
    summary,
    byModule,
    familyStatuses
  } = report;
  const getStatusColor = (status: FamilyCoverageStatus['status']) => {
    switch (status) {
      case 'complete':
        return 'text-emerald-500';
      case 'partial':
        return 'text-amber-500';
      case 'missing_support':
        return 'text-rose-500';
      case 'missing_questions':
        return 'text-blue-500';
      case 'empty':
        return 'text-slate-300';
    }
  };
  const getStatusIcon = (status: FamilyCoverageStatus['status']) => {
    switch (status) {
      case 'complete':
        return CheckCircle2;
      case 'partial':
        return AlertTriangle;
      case 'missing_support':
        return AlertOctagon;
      case 'missing_questions':
        return FileQuestion;
      case 'empty':
        return XCircle;
    }
  };
  return <div className="space-y-8" data-id="element-1616">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1617">
        <SummaryCard title="Complete" count={summary.complete} total={report.totalFamilies} icon={CheckCircle2} color="emerald" data-id="element-1618" />

        <SummaryCard title="Partial" count={summary.partial} total={report.totalFamilies} icon={AlertTriangle} color="amber" data-id="element-1619" />

        <SummaryCard title="No Support" count={summary.missingSupport} total={report.totalFamilies} icon={AlertOctagon} color="rose" data-id="element-1620" />

        <SummaryCard title="No Questions" count={summary.missingQuestions} total={report.totalFamilies} icon={FileQuestion} color="blue" data-id="element-1621" />

        <SummaryCard title="Empty" count={summary.empty} total={report.totalFamilies} icon={XCircle} color="slate" data-id="element-1622" />
      </div>

      {/* Module Breakdown */}
      <Card className="p-6 border-slate-200" data-id="element-1623">
        <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1624">
          <LayoutList className="w-5 h-5 text-indigo-600" data-id="element-1625" />
          Module Breakdown
        </h3>
        <div className="overflow-x-auto" data-id="element-1626">
          <table className="w-full text-sm text-left" data-id="element-1627">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1628">
              <tr data-id="element-1629">
                <th className="px-4 py-3 font-bold" data-id="element-1630">Module</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1631">Total</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1632">Supported</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1633">Missing</th>
                <th className="px-4 py-3 font-bold" data-id="element-1634">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1635">
              {Object.entries(byModule).map(([mod, stats]) => {
              const percentage = Math.round(stats.supported / stats.total * 100);
              return <tr key={mod} className="hover:bg-slate-50/50" data-id="element-1636">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-1637">
                      {mod}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600" data-id="element-1638">
                      {stats.total}
                    </td>
                    <td className="px-4 py-3 text-center text-emerald-600 font-bold" data-id="element-1639">
                      {stats.supported}
                    </td>
                    <td className="px-4 py-3 text-center text-rose-600 font-bold" data-id="element-1640">
                      {stats.missing.length}
                    </td>
                    <td className="px-4 py-3" data-id="element-1641">
                      <div className="flex items-center gap-3" data-id="element-1642">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1643">
                          <div className={`h-full rounded-full ${percentage === 100 ? 'bg-emerald-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                        width: `${percentage}%`
                      }} data-id="element-1644" />
                        </div>
                        <span className="text-xs font-bold text-slate-500 w-8 text-right" data-id="element-1645">
                          {percentage}%
                        </span>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Critical Issues List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1646">
        {/* Missing Support */}
        <Card className="p-6 border-rose-200 bg-rose-50/10" data-id="element-1647">
          <h3 className="font-bold text-rose-900 mb-4 flex items-center gap-2" data-id="element-1648">
            <AlertOctagon className="w-5 h-5 text-rose-600" data-id="element-1649" />
            Missing Support ({report.questionsWithoutSupport.length})
          </h3>
          <p className="text-xs text-rose-700 mb-4" data-id="element-1650">
            These families have runnable questions but NO support content
            defined.
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1651">
            {report.questionsWithoutSupport.length === 0 ? <div className="text-sm text-emerald-600 font-medium flex items-center gap-2" data-id="element-1652">
                <CheckCircle2 className="w-4 h-4" data-id="element-1653" /> All active families have
                support!
              </div> : report.questionsWithoutSupport.map(id => <div key={id} className="flex items-center justify-between p-2 bg-white rounded border border-rose-100 text-sm" data-id="element-1654">
                  <span className="font-mono font-bold text-slate-700" data-id="element-1655">
                    {id}
                  </span>
                  <Badge variant="neutral" size="sm" data-id="element-1656">
                    Needs Entry
                  </Badge>
                </div>)}
          </div>
        </Card>

        {/* Missing Questions */}
        <Card className="p-6 border-blue-200 bg-blue-50/10" data-id="element-1657">
          <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2" data-id="element-1658">
            <FileQuestion className="w-5 h-5 text-blue-600" data-id="element-1659" />
            Unused Support ({report.supportWithoutQuestions.length})
          </h3>
          <p className="text-xs text-blue-700 mb-4" data-id="element-1660">
            Support content exists, but no questions use this family ID yet.
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1661">
            {report.supportWithoutQuestions.length === 0 ? <div className="text-sm text-slate-500 italic" data-id="element-1662">
                No orphaned support entries.
              </div> : report.supportWithoutQuestions.map(id => <div key={id} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100 text-sm" data-id="element-1663">
                  <span className="font-mono font-bold text-slate-700" data-id="element-1664">
                    {id}
                  </span>
                  <Badge variant="neutral" size="sm" data-id="element-1665">
                    Needs Questions
                  </Badge>
                </div>)}
          </div>
        </Card>
      </div>

      {/* Detailed Family Status Table */}
      <Card className="p-6 border-slate-200" data-id="element-1666">
        <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1667">
          <PieChart className="w-5 h-5 text-indigo-600" data-id="element-1668" />
          Detailed Family Status
        </h3>
        <div className="overflow-x-auto" data-id="element-1669">
          <table className="w-full text-sm text-left" data-id="element-1670">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1671">
              <tr data-id="element-1672">
                <th className="px-4 py-3 font-bold" data-id="element-1673">Family ID</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1674">Status</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1675">Questions</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1676">Hints</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1677">Visual</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1678">Explain</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1679">
              {familyStatuses.map(status => {
              const StatusIcon = getStatusIcon(status.status);
              const statusClass = getStatusColor(status.status);
              return <tr key={status.familyId} className="hover:bg-slate-50/50" data-id="element-1680">
                    <td className="px-4 py-3" data-id="element-1681">
                      <div className="font-mono font-bold text-slate-700 text-xs" data-id="element-1682">
                        {status.familyId}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]" data-id="element-1683">
                        {status.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1684">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${statusClass.replace('text-', 'bg-').replace('500', '50')} ${statusClass} border-${statusClass.split('-')[1]}-200`} data-id="element-1685">
                        <StatusIcon className="w-3.5 h-3.5" data-id="element-1686" />
                        {status.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1687">
                      {status.questionCount > 0 ? <span className="font-bold text-slate-700" data-id="element-1688">
                          {status.questionCount}
                        </span> : <span className="text-slate-300" data-id="element-1689">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1690">
                      {status.hasHints ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1691" /> : status.requiresHints ? <span className="text-rose-500 font-bold text-xs" data-id="element-1692">
                          REQ
                        </span> : <span className="text-slate-300" data-id="element-1693">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1694">
                      {status.hasVisual ? <Eye className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1695" /> : status.requiresVisual ? <span className="text-rose-500 font-bold text-xs" data-id="element-1696">
                          REQ
                        </span> : <span className="text-slate-300" data-id="element-1697">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1698">
                      {status.hasExplanation ? <HelpCircle className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1699" /> : <span className="text-slate-300" data-id="element-1700">-</span>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}
function SummaryCard({
  title,
  count,
  total,
  icon: Icon,
  color
}: {
  title: string;
  count: number;
  total: number;
  icon: React.ElementType;
  color: 'emerald' | 'amber' | 'rose' | 'blue' | 'slate';
}) {
  const colorClasses: Record<string, string> = {
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    rose: 'text-rose-600 bg-rose-50',
    blue: 'text-blue-600 bg-blue-50',
    slate: 'text-slate-600 bg-slate-50'
  };
  return <Card className="p-4 flex items-center justify-between border-slate-200" data-id="element-1701">
      <div data-id="element-1702">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1703">
          {title}
        </div>
        <div className="text-2xl font-bold text-slate-900" data-id="element-1704">{count}</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-1705">
          {Math.round(count / total * 100)}% of total
        </div>
      </div>
      <div className={`p-3 rounded-full ${colorClasses[color]}`} data-id="element-1706">
        <Icon className="w-6 h-6" data-id="element-1707" />
      </div>
    </Card>;
}